import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from "../api";
import type { ID } from "../id";
import type { SystemRole } from "../../auth/queries";

export type NoticeSeverity = "info" | "warn" | "err";
export type NoticeDisplay = "banner" | "popup";
export type NoticeAudience = "public" | "signed_in" | "both";

/** What a reader receives: the server has already decided they see it. */
export interface ShownNotice {
  id: ID;
  title: string;
  body: string;
  severity: NoticeSeverity;
  display: NoticeDisplay;
  /** Whether a lišta in the app may be closed with ×. */
  dismissible: boolean;
}

/** The administrator's full view of an oznámení. */
export interface Notice extends ShownNotice {
  audience: NoticeAudience;
  starts_at: Dayjs | null;
  ends_at: Dayjs | null;
  roles: SystemRole[];
  team_ids: ID[];
  federation_ids: ID[];
  created_at: Dayjs;
  updated_at: Dayjs;
}

interface NoticesResponse<T> {
  notices: T[];
}

/**
 * How often the LOGIN PAGE asks for new notices. Only there: that request
 * carries no session. Inside the app a timed poll would slide the session
 * on every tick (RequireSession touches it), so a tab left open, an
 * impersonation included, would never expire. The app fetches on window
 * focus and on navigation instead, which is when somebody is there to read.
 */
const PUBLIC_POLL_MS = 2 * 60_000;

/** The login page's notices. No session: served to anybody. */
export function usePublicNotices() {
  return useQuery<ShownNotice[]>({
    queryKey: ["notices", "public"],
    queryFn: async () =>
      (await apiGet<NoticesResponse<ShownNotice>>("/notices/public")).notices,
    staleTime: 0,
    refetchInterval: PUBLIC_POLL_MS,
  });
}

/**
 * What the signed-in user is shown, targeting already applied. No interval
 * (see PUBLIC_POLL_MS): a focus always refetches, and a navigation does
 * once the minute is up, since every page remounts the strip.
 */
export function useMyNotices() {
  return useQuery<ShownNotice[]>({
    queryKey: ["notices", "mine"],
    queryFn: async () => (await apiGet<NoticesResponse<ShownNotice>>("/notices")).notices,
    staleTime: 60_000,
    refetchOnWindowFocus: "always",
  });
}

/** Every notice, for the admin page. */
export function useAllNotices() {
  return useQuery<Notice[]>({
    queryKey: ["notices", "all"],
    queryFn: async () => (await apiGet<NoticesResponse<Notice>>("/notices/all")).notices,
  });
}

export interface NoticePayload {
  title: string;
  body: string;
  severity: NoticeSeverity;
  display: NoticeDisplay;
  audience: NoticeAudience;
  dismissible: boolean;
  /** ISO strings: outgoing bodies are never Dayjs. */
  starts_at: string | null;
  ends_at: string | null;
  roles: SystemRole[];
  team_ids: ID[];
  federation_ids: ID[];
}

export function useSaveNotice(id: ID | null) {
  const qc = useQueryClient();
  return useMutation<Notice, ApiError, NoticePayload>({
    mutationFn: (payload) =>
      id ? apiPut<Notice>(`/notices/${id}`, payload) : apiPost<Notice>("/notices", payload),
    // The dialog renders the refusal.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notices"] });
    },
  });
}

export function useDeleteNotice() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID>({
    mutationFn: (id) => apiDelete<void>(`/notices/${id}`),
    // Reported inside the confirm dialog.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notices"] });
    },
  });
}
